import type { CellClassParams, ColDef, GridOptions, SpanRowsParams } from "ag-grid-community"
import dayjs from "dayjs"

import { getRequiredEmptyCellClassRule, pricingCalc } from "../../../utils"
import { GridCells } from "../GridCells"
import type { PricingGridRow } from "../types"
import { FORMATTERS } from "../utils"

import { COLUMN_PROPS, LIST_PRICE_LABEL, MARGIN_THRESHOLDS, NON_EDITABLE_CELL_CLASS } from "./constants"

import { DATE_FORMAT, QUANTITY_RENDER_PRECISION } from "@/constants/formats"
import { formatEnumValue } from "@/constants/pricingTiers"
import { PriceChangeType } from "@/graphql/codegen/graphql"
import type { PriceChangeTableVariant } from "@/screens/Pricing/constants"
import { AddressFormat } from "@/types/common"
import { isAfterDate } from "@/utils/date"
import { formatLocationAddress, isNil } from "@/utils/utils"

export const GRID_DEFAULT_OPTIONS: GridOptions<PricingGridRow> = {
  enableCellSpan: true,
  suppressMovableColumns: true,
  stopEditingWhenCellsLoseFocus: true,
  selectionColumnDef: {
    pinned: "left",
    width: 50,
    sortable: false,
    resizable: false,
    headerClass: NON_EDITABLE_CELL_CLASS,
    headerComponent: GridCells.SelectionHeader,
    cellClass: "pricing-selection-cell",
    cellRenderer: GridCells.Selection,
  },
  domLayout: "autoHeight",
  dataTypeDefinitions: {
    dateString: {
      baseDataType: "dateString",
      extendsDataType: "dateString",
      valueParser: ({ newValue }) => newValue,
      valueFormatter: ({ value }) => {
        if (!value) return ""
        const parsed = dayjs(value, DATE_FORMAT.ISO_18601, true)
        return parsed.isValid() ? parsed.format(DATE_FORMAT.US) : value
      },
      dataTypeMatcher: (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value),
      dateParser: (value) => {
        if (!value) return undefined
        const parsed = dayjs(value, DATE_FORMAT.ISO_18601, true)
        return parsed.isValid() ? parsed.toDate() : undefined
      },
      dateFormatter: (date) => {
        if (!date) return undefined
        return dayjs(date).format(DATE_FORMAT.ISO_18601)
      },
    },
  },
}

interface PricingColumnMeta {
  priceChangeType?: PriceChangeType
  isQuoteSource?: boolean
}

type PricingColDef = ColDef<PricingGridRow> & {
  context?: PricingColumnMeta
}

const getNewMarginCellClass = (params: CellClassParams<PricingGridRow, number>): string => {
  const value = params.value as number
  if (value <= MARGIN_THRESHOLDS.LOW) return "bg-red-100"
  if (value <= MARGIN_THRESHOLDS.MEDIUM) return "bg-yellow-100"
  return "bg-green-100"
}

const getReadonlyCellClassProp = (readonly?: boolean): Pick<PricingColDef, "cellClass"> | Record<string, never> =>
  readonly ? { cellClass: NON_EDITABLE_CELL_CLASS } : {}

export const getPricingEditColumns = ({
  uomName,
  onAddPriceTier,
  onClearPriceTier,
  onEditSurcharge,
  isRowNew = () => false,
  isRowDefaulted = () => false,
  isQuoteSource = false,
  variant,
  getGroupIndex,
  readonly,
  priceChangeType,
  modifiedRows,
  isUomChanged,
}: {
  uomName: string
  onAddPriceTier: (rowIndex: number) => void
  onClearPriceTier: (rowIndex: number) => void
  onEditSurcharge: (rowIndex: number) => void
  isRowNew?: (row: PricingGridRow) => boolean
  isRowDefaulted?: (row: PricingGridRow) => boolean
  isQuoteSource?: boolean
  variant?: PriceChangeTableVariant
  getGroupIndex: (priceChangeItemId: string) => number
  readonly?: boolean
  priceChangeType: PriceChangeType
  modifiedRows: Set<string>
  isUomChanged: boolean
}): ColDef<PricingGridRow>[] => {
  const groupAltCellClassRules: ColDef<PricingGridRow>["cellClassRules"] = {
    "!bg-gray-100": ({ data }) => {
      if (!data) return false
      return getGroupIndex(data.item.priceChangeItemId) % 2 === 1
    },
    [NON_EDITABLE_CELL_CLASS]: () => Boolean(readonly),
  }

  const spanWithinGroup = ({ nodeA, nodeB, valueA, valueB }: SpanRowsParams<PricingGridRow>): boolean => {
    const groupA = nodeA?.data && getGroupIndex(nodeA.data.item.priceChangeItemId)
    const groupB = nodeB?.data && getGroupIndex(nodeB.data.item.priceChangeItemId)
    return groupA === groupB && valueA === valueB
  }

  const cols: PricingColDef[] = [
    {
      colId: "customer",
      context: {
        priceChangeType: PriceChangeType.SellingPrice,
      },
      headerName: "Customer",
      ...COLUMN_PROPS.DEFAULT,
      headerClass: NON_EDITABLE_CELL_CLASS,
      minWidth: 300,
      pinned: "left",
      spanRows: spanWithinGroup,
      valueGetter: ({ data }) => data?.item?.customer?.name ?? LIST_PRICE_LABEL,
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "shipTo",
      headerName: "Ship To",
      ...COLUMN_PROPS.DEFAULT,
      context: {
        priceChangeType: PriceChangeType.PurchasingCost,
      },
      headerClass: NON_EDITABLE_CELL_CLASS,
      minWidth: 400,
      pinned: "left",
      spanRows: spanWithinGroup,
      valueGetter: ({ data }) => formatLocationAddress(data?.shipToWarehouseArea?.location, AddressFormat.Shipping),
      cellClass: NON_EDITABLE_CELL_CLASS,
      cellClassRules: groupAltCellClassRules,
    },
    {
      /**
       * The following warning is displayed. Functionality works, but violates an ag-grid constraint. Consider refactoring to avoid this if possible.
       * AG Grid: 'spanRows' requires 'editable' to be one of [false, undefined].
       */
      colId: "freight",
      headerName: "Freight",
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.SPANNED,
      ...(priceChangeType === PriceChangeType.PurchasingCost ? COLUMN_PROPS.FLEX : {}),
      headerClass: NON_EDITABLE_CELL_CLASS,
      valueGetter: ({ data }) => data?.item?.freightTerms ?? "",
      valueFormatter: ({ value }) => formatEnumValue(value ?? ""),
      width: 160,
      cellClass: NON_EDITABLE_CELL_CLASS,
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "effectiveDate",
      headerName: "Effective",
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.DATE,
      ...COLUMN_PROPS.SPANNED,
      ...COLUMN_PROPS.FLEX,
      headerClass: NON_EDITABLE_CELL_CLASS,
      headerComponent: GridCells.RequiredColumnHeader,
      editable: !readonly,
      valueGetter: ({ data }) => data?.item?.effectiveDate ?? "",
      valueSetter: ({ data, newValue }) => {
        const effectiveDate = newValue || null
        const expirationDate = data.item.expirationDate
        data.item.effectiveDate = effectiveDate

        if (effectiveDate && expirationDate && isAfterDate(effectiveDate, expirationDate)) {
          data.item.expirationDate = null
        }
        return true
      },
      cellEditorParams: {
        min: dayjs().format(DATE_FORMAT.ISO_18601),
      },
      cellClassRules: {
        ...groupAltCellClassRules,
        "text-tertiary-green-700": ({ data }) => Boolean(data) && isRowDefaulted(data as PricingGridRow),
        "pricing-required-empty": getRequiredEmptyCellClassRule((row) => !row.item.effectiveDate, {
          readonly,
          modifiedRows,
          isUomChanged,
        }),
      },
    },
    {
      colId: "expirationDate",
      headerName: "Expiration",
      editable: !readonly,
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.DATE,
      ...COLUMN_PROPS.SPANNED,
      ...COLUMN_PROPS.FLEX,
      valueGetter: ({ data }) => data?.item?.expirationDate ?? "",
      valueSetter: ({ data, newValue }) => {
        data.item.expirationDate = newValue || null
        return true
      },
      cellClassRules: groupAltCellClassRules,
      cellEditorParams: ({ data }: { data: PricingGridRow | undefined }) => {
        const currentDate = dayjs().format(DATE_FORMAT.ISO_18601)
        const effectiveDate = data?.item?.effectiveDate

        return {
          min:
            effectiveDate && isAfterDate(effectiveDate, currentDate)
              ? dayjs(effectiveDate).format(DATE_FORMAT.ISO_18601)
              : currentDate,
        }
      },
    },
    {
      colId: "last12",
      headerName: "Last 12",
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) => data?.item?.last12MonthsQty ?? null,
      valueFormatter: FORMATTERS.uomSuffix(uomName),
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "qty",
      headerName: "Qty",
      ...COLUMN_PROPS.DEFAULT,
      editable: !readonly,
      valueFormatter: FORMATTERS.uomSuffix(uomName, QUANTITY_RENDER_PRECISION),
      headerComponent: GridCells.RequiredColumnHeader,
      ...COLUMN_PROPS.NUMERIC,
      ...COLUMN_PROPS.FLEX,
      ...getReadonlyCellClassProp(readonly),
      cellEditorParams: { min: 0, precision: QUANTITY_RENDER_PRECISION },
      cellClassRules: {
        "pricing-required-empty": getRequiredEmptyCellClassRule((row) => !row.qty, {
          readonly,
          modifiedRows,
          isUomChanged,
        }),
      },
    },
    {
      colId: "price",
      headerName: "Price",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) => data?.previousPrice ?? null,
      valueFormatter: FORMATTERS.financial,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
      ...COLUMN_PROPS.FLEX,
    },
    {
      colId: "profit",
      headerName: "Profit",
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) =>
        !isNil(data?.previousPrice) && !isNil(data?.previousAcceptedCost)
          ? pricingCalc.currentProfit({
              currentPrice: data.previousPrice,
              acceptedCost: data.previousAcceptedCost,
            })
          : null,
      valueFormatter: FORMATTERS.financial,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      colId: "margin",
      headerName: "Margin",
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) =>
        !isNil(data?.previousPrice) && !isNil(data?.previousAcceptedCost)
          ? pricingCalc.currentMargin({
              currentPrice: data.previousPrice,
              acceptedCost: data.previousAcceptedCost,
            })
          : null,
      valueFormatter: FORMATTERS.percentSuffix,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "newPrice",
      headerName: "New Price",
      ...COLUMN_PROPS.DEFAULT,
      editable: !readonly,
      valueGetter: ({ data }) => data?.newPrice ?? null,
      valueFormatter: FORMATTERS.financial,
      ...COLUMN_PROPS.NUMERIC,
      ...COLUMN_PROPS.PRICE,
      ...COLUMN_PROPS.FLEX,
      ...getReadonlyCellClassProp(readonly),
    },
    {
      field: "newProfit",
      headerName: "New Profit",
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) =>
        !isNil(data?.newPrice) && !isNil(data?.acceptedCost)
          ? pricingCalc.currentProfit({
              currentPrice: data.newPrice,
              acceptedCost: data.acceptedCost,
            })
          : null,
      valueFormatter: FORMATTERS.financial,
      cellClass: NON_EDITABLE_CELL_CLASS,
      ...COLUMN_PROPS.NUMERIC,
    },
    {
      field: "newMargin",
      headerName: "New Margin",
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.NUMERIC,
      valueGetter: ({ data }) =>
        !isNil(data?.newPrice) && !isNil(data?.acceptedCost)
          ? pricingCalc.currentMargin({
              currentPrice: data.newPrice,
              acceptedCost: data.acceptedCost,
            })
          : null,
      valueFormatter: FORMATTERS.percentSuffix,
      cellClass: getNewMarginCellClass,
    },
    {
      colId: "surcharge",
      headerName: "Surcharge",
      ...COLUMN_PROPS.DEFAULT,
      valueGetter: ({ data }) =>
        pricingCalc.surcharge({
          surcharge1Price: data?.surcharge1Price,
          surcharge2Price: data?.surcharge2Price,
          surcharge3Price: data?.surcharge3Price,
        }),
      valueFormatter: FORMATTERS.financial,
      ...COLUMN_PROPS.NUMERIC,
      ...COLUMN_PROPS.FLEX,
      ...getReadonlyCellClassProp(readonly),
    },

    {
      colId: "bb",
      headerName: "BB",
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      editable: !readonly,
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.SPANNED,
      cellRenderer: GridCells.BillBack,
      valueGetter: ({ data }) => data?.item?.billBack ?? null,
      valueSetter: ({ data, newValue }) => {
        if (newValue === data.item.billBack) return false

        data.item.previousBillBack = data.item.billBack
        data.item.billBack = newValue
        return true
      },
      ...COLUMN_PROPS.NUMERIC,
      ...COLUMN_PROPS.PRICE,
      cellClassRules: groupAltCellClassRules,
    },
    {
      colId: "changeNote",
      headerName: "Note",
      editable: !readonly,
      context: {
        isQuoteSource: false,
        priceChangeType: PriceChangeType.SellingPrice,
      },
      valueGetter: ({ data }) => data?.item?.changeNote ?? null,
      valueSetter: ({ data, newValue }) => {
        if (newValue === data.item.changeNote) return false

        data.item.changeNote = newValue
        return true
      },
      ...COLUMN_PROPS.DEFAULT,
      ...COLUMN_PROPS.FLEX,
      ...COLUMN_PROPS.SPANNED,
      ...getReadonlyCellClassProp(readonly),
    },
    {
      headerName: "",
      ...COLUMN_PROPS.DEFAULT,
      cellRenderer: GridCells.RowActions,
      cellRendererParams: { onAddPriceTier, onClearPriceTier, onEditSurcharge, isRowNew, variant, readonly },
      width: 60,
      suppressMovable: true,
      pinned: "right",
      hide: readonly,
    },
  ]

  return cols.filter((col) => {
    const { priceChangeType: columnPriceChangeType, isQuoteSource: columnIsQuoteSource } = col.context ?? {}

    const matchesPriceChangeType = !columnPriceChangeType || columnPriceChangeType === priceChangeType

    const matchesQuoteSource = columnIsQuoteSource === undefined || columnIsQuoteSource === isQuoteSource

    return matchesPriceChangeType && matchesQuoteSource
  })
}
